import React, { useEffect, useRef, useState } from 'react';
import { flattenToAppURL } from '@plone/volto/helpers';
import { isColorDark, getButtonClasses } from '../../../config/colors';

import './multicard.css';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';

const getImageURL = (image) => {
  if (typeof image === 'string') {
    return `${flattenToAppURL(image)}/@@images/image/large`;
  }
  const imageItem = Array.isArray(image) ? image[0] : image;
  if (imageItem?.['@id']) return `${flattenToAppURL(imageItem['@id'])}/@@images/image/large`;
  if (imageItem?.download) return flattenToAppURL(imageItem.download);
  return null;
};

const getHref = (url, anchor) => {
  const base = typeof url === 'string' ? url : (Array.isArray(url) ? url[0]?.['@id'] : url?.['@id']);
  return anchor?.trim() ? (base ? `${base}${anchor}` : anchor) : base || '#';
};

const isExternalLink = (href) => {
  if (!href) return false;
  if (typeof window === 'undefined') return false;
  try {
    const host = window.location.hostname;
    return href.startsWith('http') && !href.includes(host);
  } catch {
    return false;
  }
};

// Arrow icon matching the bi-arrow-right-short used in the Embla carousel
const ArrowIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    fill="currentColor"
    viewBox="0 0 16 16"
    aria-hidden="true"
    focusable="false"
  >
    <path
      fillRule="evenodd"
      d="M4 8a.5.5 0 0 1 .5-.5h5.793L8.146 5.354a.5.5 0 1 1 .708-.708l3 3a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708-.708L10.293 8.5H4.5A.5.5 0 0 1 4 8z"
    />
  </svg>
);

const CountUp = ({ end = 0, duration = 1500, formatK = false }) => {
  const [value, setValue] = useState(0);
  const startRef = useRef(null);
  const endRef = useRef(Number(end) || 0);

  useEffect(() => {
    endRef.current = Number(end) || 0;
    setValue(0);
    startRef.current = null;

    const step = (ts) => {
      if (!startRef.current) startRef.current = ts;
      const progress = Math.min((ts - startRef.current) / duration, 1);
      let next = Math.floor(progress * endRef.current);
      if (formatK && endRef.current >= 1000) {
        next = next - (next % 1000);
      }
      setValue(next);
      if (progress < 1) requestAnimationFrame(step);
    };
    const raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [end, duration]);

  return (
    <span className="stat-value-number">
      {formatK && endRef.current >= 1000
        ? `${Math.max(1, Math.floor(value / 1000)).toLocaleString()}k`
        : value.toLocaleString()}
    </span>
  );
};

const useIsMobile = () => {
  const [isMobile, setIsMobile] = React.useState(false);
  React.useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener ? mq.addEventListener('change', update) : mq.addListener(update);
    return () => (mq.removeEventListener ? mq.removeEventListener('change', update) : mq.removeListener(update));
  }, []);
  return isMobile;
};

const MobileCarousel = ({ enabled, autoplay = true, showDots = true, controlsColor = 'light', children }) => {
  const isMobile = useIsMobile();
  const plugins = React.useMemo(() => (autoplay ? [Autoplay({ delay: 3500, stopOnMouseEnter: true })] : []), [autoplay]);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, plugins);
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const slides = React.Children.toArray(children);

  React.useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on('select', onSelect);
    onSelect();
  }, [emblaApi]);

  if (!enabled || !isMobile) return <>{children}</>;
  return (
    <div className={`embla carousel-controls-${controlsColor}`}>
      <div className="embla__viewport" ref={emblaRef}>
        <div className="embla__container">
          {slides.map((slide, i) => (
            <div className="embla__slide" key={i}>{slide}</div>
          ))}
        </div>
      </div>
      {showDots && (
        <div className="embla__dots">
          {slides.map((_, i) => (
            <button
              key={i}
              className={`embla__dot ${i === selectedIndex ? 'is-selected' : ''}`}
              onClick={() => emblaApi && emblaApi.scrollTo(i)}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const View = ({ data }) => {
  const headerAlignmentClass = `align-${data.headerAlignment || 'left'}`;
  const blockAlignmentClass = `align-${data.blockAlignment || 'left'}`;
  const columnClass = `columns-${data.columns || 3}`;

  // Generate anchor ID for TOC
  const generateSlug = (text) => {
    if (!text) return '';
    return text
      .toLowerCase()
      .replace(/<[^>]*>/g, '')
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const anchorId = generateSlug(data.headerText);

  // Block background color via unified colors.js
  const blockBgValue =
    data.blockBackgroundColor && data.blockBackgroundColor !== 'none'
      ? data.blockBackgroundColor
      : null;
  const blockStyle = blockBgValue
    ? { backgroundColor: blockBgValue, padding: '3rem 0' }
    : {};

  const controlsColor = data.carouselControlsColor || 'light';

  // Custom class
  const customClass = data.customClass?.trim() ? ` ${data.customClass.trim()}` : '';

  const renderHeader = () => {
    const hasText = data.preheaderText?.trim() || data.headerText?.trim() || data.descriptionText?.trim();
    const showViewAll = data.showViewAll && data.viewAllText?.trim() && data.viewAllUrl;

    if (!hasText && !showViewAll) return null;

    const viewAllHref = showViewAll ? getHref(data.viewAllUrl, null) : null;
    const viewAllExternal = viewAllHref ? isExternalLink(viewAllHref) : false;
    const { className: viewAllClass, style: viewAllStyle } = getButtonClasses(data.viewAllStyle, 'multi-card-view-all');

    return (
      <div
        className={`multi-card-block-header ${headerAlignmentClass}${showViewAll ? ' has-view-all' : ''}`}
        id={anchorId || undefined}
      >
        <div className="multi-card-block-header-text-wrap">
          {data.preheaderText?.trim() && (
            <div className="multi-card-block-preheader-text">{data.preheaderText}</div>
          )}
          {data.headerText?.trim() && (
            <h2
              className="multi-card-block-header-text heading"
              dangerouslySetInnerHTML={{
                __html: data.headerText.replace(/\n/g, '<br />'),
              }}
            />
          )}
          {data.descriptionText?.trim() && (
            <p className="multi-card-block-description">{data.descriptionText}</p>
          )}
        </div>
        {showViewAll && (
          <a
            href={viewAllHref}
            className={viewAllClass}
            style={viewAllStyle}
            target={viewAllExternal ? '_blank' : '_self'}
            rel={viewAllExternal ? 'noopener noreferrer' : undefined}
          >
            {data.viewAllText}
          </a>
        )}
      </div>
    );
  };

  if (data.useStatistics) {
    return (
      <div className={`multi-card-block${customClass}`} style={blockStyle}>
        {renderHeader()}
        <div className={`multi-card-container stats ${blockAlignmentClass} ${columnClass}`}>
          <MobileCarousel
            enabled={!!data.mobileCarousel}
            autoplay={data.mobileCarouselAutoplay !== false}
            showDots={data.mobileCarouselDots !== false}
            controlsColor={controlsColor}
          >
            {(data.statistics || []).map((stat, idx) => {
              const href = getHref(stat.url, stat.anchor);
              const external = isExternalLink(href);
              const content = (
                <div className="multi-stat" key={idx}>
                  {stat.label?.trim() && <div className="stat-label">{stat.label}</div>}
                  <div className="stat-value">
                    <CountUp
                      end={stat.value}
                      duration={data.statAnimationMs || 6000}
                      formatK={!!data.statsFormatK}
                    />
                    {stat.suffix?.trim() && (
                      <span className="stat-suffix">{stat.suffix}</span>
                    )}
                  </div>
                  {stat.extraInfo?.trim() && (
                    <div className="stat-extra">{stat.extraInfo}</div>
                  )}
                </div>
              );
              return stat.url || stat.anchor ? (
                <a
                  key={idx}
                  className="multi-stat-link"
                  href={href}
                  target={external ? '_blank' : '_self'}
                  rel={external ? 'noopener noreferrer' : undefined}
                >
                  {content}
                </a>
              ) : (
                content
              );
            })}
          </MobileCarousel>
        </div>
      </div>
    );
  }

  return (
    <div className={`multi-card-block${customClass}`} style={blockStyle}>
      {renderHeader()}
      <div className={`multi-card-container ${blockAlignmentClass} ${columnClass}`}>
        <MobileCarousel
          enabled={!!data.mobileCarousel}
          autoplay={data.mobileCarouselAutoplay !== false}
          showDots={data.mobileCarouselDots !== false}
          controlsColor={controlsColor}
        >
          {(data.items || []).map((item, index) => {
            const imageUrl = getImageURL(item.image);
            const href = getHref(item.url, item.anchor);
            const external = isExternalLink(href);
            const imagePosition = data.imagePosition || 'background';
            const imageClass = `image-${imagePosition}`;

            // Parse background color from unified colors.js
            // isColorDark() checks the COLOR_LIST by value
            let backgroundColor = null;
            let themeClass = '';

            if (imagePosition !== 'background' && data.cardBackgroundColor?.trim()) {
              backgroundColor = data.cardBackgroundColor;
              themeClass = isColorDark(data.cardBackgroundColor)
                ? 'theme-dark'
                : 'theme-light';
            }

            const cardStyle = {};
            if (imagePosition === 'background' && imageUrl) {
              cardStyle.backgroundImage = `url(${imageUrl})`;
            } else if (backgroundColor) {
              cardStyle.backgroundColor = backgroundColor;
            }

            const buttonStyle = item.buttonStyle || 'primary';
            const isArrow = buttonStyle === 'arrow';
            const { className: btnClass, style: btnStyle } = getButtonClasses(buttonStyle, 'button');

            return (
              <div
                key={index}
                className={`multi-card ${imageClass} ${themeClass}`.trim()}
                style={cardStyle}
              >
                {imagePosition === 'background' && <div className="multi-card-overlay" />}

                {imagePosition === 'above-card' && imageUrl && (
                  <img src={imageUrl} alt={item.title || ''} className="multi-card-image" />
                )}

                <div className="multi-card-content">
                  {item.preheader?.trim() && (
                    <div className="multi-card-preheader">{item.preheader}</div>
                  )}
                  {item.title?.trim() && <h2>{item.title}</h2>}

                  {imagePosition === 'above-content' && imageUrl && (
                    <img src={imageUrl} alt={item.title || ''} className="multi-card-image" />
                  )}

                  {item.description?.trim() && <p>{item.description}</p>}

                  {imagePosition === 'below-content' && imageUrl && (
                    <img src={imageUrl} alt={item.title || ''} className="multi-card-image" />
                  )}

                  {item.linkText?.trim() && (
                    <a
                      href={href}
                      target={external ? '_blank' : '_self'}
                      rel={external ? 'noopener noreferrer' : undefined}
                      className={btnClass}
                      style={btnStyle}
                      aria-label={isArrow ? item.linkText : undefined}
                    >
                      {isArrow ? <ArrowIcon /> : item.linkText}
                    </a>
                  )}
                </div>

                {imagePosition === 'below-card' && imageUrl && (
                  <img src={imageUrl} alt={item.title || ''} className="multi-card-image" />
                )}
              </div>
            );
          })}
        </MobileCarousel>
      </div>
    </div>
  );
};

export default View;
